// src/hooks/useNotifications.js
//
// React Query hooks for the Notification Center.
// Provides queries + mutations for in-app notifications,
// preferences, and scheduled reminders.

import { useQuery, useMutation, useQueryClient } from 'react-query'
import toast from 'react-hot-toast'
import { notificationCenterApi } from '@/api/notifications'

// ── Notifications ───────────────────────────────────────────────────────────

export function useNotifications(params) {
  return useQuery(
    ['notifications', params],
    async () => {
      const { data } = await notificationCenterApi.list(params)
      return data.data
    },
    { keepPreviousData: true }
  )
}

export function useUnreadCount() {
  return useQuery(
    ['notifications-unread-count'],
    async () => {
      const { data } = await notificationCenterApi.unreadCount()
      return data.data.count
    },
    {
      // Polling is no longer needed — real-time updates are handled
      // by the WebSocket subscription in NotificationBell.jsx.
      // Keep a fallback refetch at 5 minutes for resilience.
      refetchInterval: 300000,
      refetchIntervalInBackground: false,
    }
  )
}

export function useMarkRead() {
  const queryClient = useQueryClient()
  return useMutation(
    async (id) => {
      const res = await notificationCenterApi.markRead(id)
      return res.data
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('notifications')
        queryClient.invalidateQueries('notifications-unread-count')
      },
      onError: (err) => toast.error(err.response?.data?.message || 'Failed to mark as read'),
    }
  )
}

export function useMarkAllRead() {
  const queryClient = useQueryClient()
  return useMutation(
    async () => {
      const res = await notificationCenterApi.markAllRead()
      return res.data
    },
    {
      onSuccess: () => {
        toast.success('All notifications marked as read')
        queryClient.invalidateQueries('notifications')
        queryClient.invalidateQueries('notifications-unread-count')
      },
      onError: (err) => toast.error(err.response?.data?.message || 'Failed to mark all as read'),
    }
  )
}

export function useDeleteNotification() {
  const queryClient = useQueryClient()
  return useMutation(
    async (id) => {
      await notificationCenterApi.delete(id)
    },
    {
      onSuccess: () => {
        toast.success('Notification deleted')
        queryClient.invalidateQueries('notifications')
        queryClient.invalidateQueries('notifications-unread-count')
      },
      onError: (err) => toast.error(err.response?.data?.message || 'Failed to delete notification'),
    }
  )
}

// ── Preferences ─────────────────────────────────────────────────────────────

export function useNotificationPreferences() {
  return useQuery(
    ['notification-preferences'],
    async () => {
      const { data } = await notificationCenterApi.getPreferences()
      return data.data
    }
  )
}

export function useUpdatePreferences() {
  const queryClient = useQueryClient()
  return useMutation(
    async (data) => {
      const res = await notificationCenterApi.updatePreferences(data)
      return res.data
    },
    {
      onSuccess: () => {
        toast.success('Preferences saved')
        queryClient.invalidateQueries('notification-preferences')
      },
      onError: (err) => toast.error(err.response?.data?.message || 'Failed to save preferences'),
    }
  )
}

// ── Scheduled Reminders (Admin) ─────────────────────────────────────────────

export function useReminders(params) {
  return useQuery(
    ['scheduled-reminders', params],
    async () => {
      const { data } = await notificationCenterApi.listReminders(params)
      return data.data
    },
    { keepPreviousData: true }
  )
}

export function useCreateReminder() {
  const queryClient = useQueryClient()
  return useMutation(
    async (data) => {
      const res = await notificationCenterApi.createReminder(data)
      return res.data
    },
    {
      onSuccess: () => {
        toast.success('Reminder scheduled')
        queryClient.invalidateQueries('scheduled-reminders')
      },
      onError: (err) => toast.error(err.response?.data?.message || 'Failed to create reminder'),
    }
  )
}

export function useCancelReminder() {
  const queryClient = useQueryClient()
  return useMutation(
    async (id) => {
      await notificationCenterApi.cancelReminder(id)
    },
    {
      onSuccess: () => {
        toast.success('Reminder cancelled')
        queryClient.invalidateQueries('scheduled-reminders')
      },
      onError: (err) => toast.error(err.response?.data?.message || 'Failed to cancel reminder'),
    }
  )
}
